import { GRIEVANCE_MOBILE_API_URL, GRIEVANCE_USER_API_URL, GRIEVANCE_API_URL, GRIEVANCE_OTP_API_BASE } from "../utils/constants";
import axios from "axios";


// OTP service (generate & verify) runs on a different port in the provided spec

// The API's user-id key name/shape varies by endpoint, so check every shape we've seen.
const extractUserId = (responseData) => {
  let data = responseData?.data ?? responseData ?? {};

  // Some create endpoints return just the new id instead of an object.
  if (typeof data === "number" || typeof data === "string") {
    return data;
  }

  if (Array.isArray(data)) {
    data = data[0] ?? {};
  }
  const user = data.user ?? {};

  const userId =
    data.userId ??
    data.userID ??
    data.UserId ??
    data.id ??
    data.Id ??
    data.grievanceUserId ??
    data.user_id ??
    user.userId ??
    user.id ??
    responseData?.userId ??
    responseData?.id ??
    null;

  if (userId == null) {
    // Helps pinpoint the real field name if the backend response shape changes.
    console.warn("Grievance user API response did not contain a recognizable userId:", responseData);
  }

  return userId;
};

export const checkGrievanceMobile = async (mobileNumber) => {
  const response = await axios.get(`${GRIEVANCE_MOBILE_API_URL}/${mobileNumber}`, {
    headers: {
      "ngrok-skip-browser-warning": "true",
    },
  });

  if (response.status !== 200) {
    throw new Error("Unable to verify this mobile number.");
  }

  return { mobileNumber, userId: extractUserId(response.data) };
};

export const getGrievanceMobileErrorMessage = () => {
  return "Unable to verify this mobile number. Please try again.";
};

/**
 * Creates a grievance user. Called only for numbers that failed the mobile-check
 * (i.e. new users), and only after OTP verification succeeds.
 */
export const createGrievanceUser = async ({ userName, mobileNo }) => {
  const response = await axios.post(
    GRIEVANCE_USER_API_URL,
    { userName, mobileNo },
    {
      headers: {
        "ngrok-skip-browser-warning": "true",
      },
    }
  );

  // This endpoint returns the created user object directly (no "success" wrapper).
  if (response.status !== 200 && response.status !== 201) {
    throw new Error("Unable to create grievance user.");
  }

  return { ...response.data, userId: extractUserId(response.data) };
};

export const getGrievanceUserErrorMessage = () => {
  return "Unable to create your grievance profile. Please try again.";
};

/**
 * Generate OTP for grievance flows.
 * payload shape example: { purposeId: 10, channelId: 3, referenceType: 3, referenceId: 1, recipientValue: "9882028352", userId: 1 }
 */
export const generateGrievanceOtp = async (payload) => {
  const response = await axios.post(`${GRIEVANCE_OTP_API_BASE}/generate`, payload, {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });

  if (response.status !== 200 && response.status !== 201) {
    throw new Error("Unable to send OTP. Please try again.");
  }

  return response.data;
};

export const getGenerateOtpErrorMessage = () => {
  return "Unable to send OTP. Please try again.";
};

/**
 * Verify OTP for grievance flows.
 * payload example: { userId: 1, purposeId: 10, otp: "258034" }
 */
export const verifyGrievanceOtp = async (payload) => {
  const response = await axios.post(`${GRIEVANCE_OTP_API_BASE}/verify`, payload, {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });

  if (response.status !== 200 && response.status !== 201) {
    throw new Error("Invalid OTP. Please try again.");
  }

  return response.data;
};

export const getVerifyOtpErrorMessage = () => {
  return "Invalid OTP. Please try again.";
};

/**
 * Submits the grievance/complaint. Called on final "Submit Grievance", after the
 * grievance user already exists (either found via mobile-check or just created).
 */
export const submitGrievance = async (payload) => {
  const { document: uploadedDocument, ...rest } = payload || {};

  const requestPayload = {
    ...rest,
    ...(uploadedDocument ? { document: uploadedDocument } : {}),
  };

  const response = await axios.post(GRIEVANCE_API_URL, requestPayload, {
    headers: {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
  });

  if (!response.data?.success) {
    throw new Error("Unable to submit grievance. Please try again.");
  }

  return response.data;
};

export const getGrievanceSubmitErrorMessage = () => {
  return "You have already submitted a grievance under this category within the last two months";
};
